// MobileDriverHome — Phase G of the Client-Surfaces Programme.
// Driver PWA: assigned jobs, accept/decline, status update, proof-of-delivery prompt.
// Reuses /api/mobile/delivery/* endpoints already in mobile_api.py.
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Truck, MapPin, Phone, Check, X, ArrowRight, Loader2, RefreshCw,
  Navigation, PoundSterling, Camera, ArrowLeft
} from 'lucide-react';
import MobileOfflineBanner from './MobileOfflineBanner';
import { queueOrSend } from '../utils/syncQueue';
import { readSharedToken } from '../utils/authTokenKeys';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_NEXT = {
  pending:          { next: 'accepted',         label: 'Accept' },
  accepted:         { next: 'out_for_delivery', label: 'Out for delivery' },
  out_for_delivery: { next: 'delivered',        label: 'Mark delivered' },
};

const STATUS_TONE = {
  pending:          'bg-warn/10 text-warn border-warn/30',
  accepted:         'bg-primary-50 text-primary-700 border-primary-200',
  out_for_delivery: 'bg-accent-50 text-accent-700 border-accent-200',
  delivered:        'bg-ok/10 text-ok border-ok/30',
  failed:           'bg-danger/10 text-danger border-danger/30',
};

const fmtMoney = (v) => (v == null ? '—' : `£${parseFloat(v).toFixed(2)}`);

const MobileDriverHome = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [view, setView] = useState('all'); // 'available' | 'active' | 'all'
  const [orgId, setOrgId] = useState(() => {
    try { return localStorage.getItem('current_organization_id') || null; } catch { return null; }
  });
  const [locationId, setLocationId] = useState(() => {
    try { return localStorage.getItem('current_location_id') || null; } catch { return null; }
  });
  // Was localStorage.getItem('token') || localStorage.getItem('mobileToken')
  // — never checked auth_token, the ONLY key a native mobile login writes
  // (audit-2026-09-07 §2, same bug as syncQueue.js's own getToken() had).
  const token = readSharedToken();

  useEffect(() => {
    try { localStorage.removeItem('mobileDriverCache'); } catch { /* ignore */ }
    const syncLocation = () => {
      try { setLocationId(localStorage.getItem('current_location_id') || null); } catch { setLocationId(null); }
    };
    window.addEventListener('locationChanged', syncLocation);
    window.addEventListener('storage', syncLocation);
    return () => {
      window.removeEventListener('locationChanged', syncLocation);
      window.removeEventListener('storage', syncLocation);
    };
  }, []);

  // Resolve orgId from /api/platform/context if it wasn't cached at login time.
  // Mirrors the AlertsCockpit pattern used by the desktop SPA.
  useEffect(() => {
    if (!token || orgId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/platform/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(ctx => {
        if (cancelled || !ctx) return;
        const id = ctx?.organizations?.[0]?.id;
        if (id) {
          setOrgId(id);
          try { localStorage.setItem('current_organization_id', id); } catch { /* ignore */ }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, orgId]);

  const headers = useMemo(() => ({
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(orgId ? { 'X-Organization-Id': orgId } : {}),
    ...(locationId ? { 'X-Location-Id': locationId } : {}),
  }), [token, orgId, locationId]);

  const reload = useCallback(async () => {
    if (!orgId || !locationId) { setLoading(false); setOrders([]); return; }
    setLoading(true);
    setOrders([]);
    setLoadError(false);
    try {
      // status=all → driver sees both available-to-claim and their own assigned jobs.
      const res = await fetch(`${API_URL}/api/mobile/delivery/orders?status=${view}`, { headers });
      if (!res.ok) throw new Error(`Delivery feed returned HTTP ${res.status}`);
      const list = await res.json();
      const arr = Array.isArray(list) ? list : (list.orders || []);
      setOrders(arr);
    } catch {
      setOrders([]);
      setLoadError(true);
    } finally { setLoading(false); }
  }, [headers, orgId, locationId, view]);

  useEffect(() => { reload(); }, [reload]);

  const transition = async (order, action) => {
    const id = order.id || order._id || order.order_id;
    setBusyId(`${id}:${action}`);
    setOrders(prev => prev.map(o => (o.id || o._id || o.order_id) === id ? { ...o, status: action } : o));

    // Server has 2 endpoints — pick the right one. Accept is its own endpoint;
    // any other transition goes through /delivery/status.
    const path = action === 'accepted'
      ? '/api/mobile/delivery/accept'
      : '/api/mobile/delivery/status';
    const body = action === 'accepted'
      ? { orderId: id }
      : { orderId: id, status: action };
    const res = await queueOrSend({ method: 'POST', path, headers, body });
    setBusyId(null);
    if (!res.ok && !res.queued) reload();
  };

  return (
    <div className="min-h-screen bg-surface-muted" data-testid="mobile-driver-page">
      <MobileOfflineBanner />
      <header className="bg-primary-500 text-surface px-4 pt-5 pb-7">
        <div className="flex items-center gap-2">
          <Link to="/mobile/dashboard" className="p-2 -ml-2 rounded-md hover:bg-white/10" data-testid="mobile-driver-back">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-overline opacity-80 flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5" /> Driver
            </div>
            <h1 className="font-display text-xl mt-0.5">Today’s jobs</h1>
          </div>
          <button onClick={reload} className="p-2 rounded-md hover:bg-white/10" data-testid="mobile-driver-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      <div className="px-4 -mt-4 pb-24 space-y-3">
        {/* View toggle — exposes both available-to-claim and the driver's own jobs */}
        <div className="bg-surface border border-surface-border rounded-md p-1 grid grid-cols-3 text-xs" data-testid="mobile-driver-tabs">
          {['available', 'active', 'all'].map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-2 py-1.5 rounded motion-organic ${view === v ? 'bg-primary-500 text-surface' : 'text-ink-muted hover:bg-surface-sunken'}`}
              data-testid={`mobile-driver-tab-${v}`}
            >
              {v === 'available' ? 'To pick up' : v === 'active' ? 'My jobs' : 'All'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-10 text-ink-muted"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : !locationId ? (
          <div className="bg-surface border border-surface-border rounded-md p-6 text-center" data-testid="mobile-driver-site-required">
            <MapPin className="w-8 h-8 mx-auto text-ink-subtle mb-2" />
            <p className="font-display text-base text-ink">Select a site to view delivery jobs</p>
          </div>
        ) : loadError ? (
          <div className="bg-surface border border-danger/30 rounded-md p-6 text-center" role="alert" data-testid="mobile-driver-load-error">
            <p className="font-display text-base text-ink">Delivery jobs could not be loaded</p>
            <p className="text-xs text-ink-muted mt-1">Check your connection and refresh. No cached customer details are shown.</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-surface border border-surface-border rounded-lg p-8 text-center" data-testid="mobile-driver-empty">
            <Truck className="w-10 h-10 mx-auto text-ink-subtle mb-3" />
            <p className="font-display text-base text-ink">No jobs assigned yet</p>
            <p className="text-xs text-ink-muted mt-1">New deliveries will appear here.</p>
          </div>
        ) : (
          orders.map(o => {
            const id = o.id || o._id || o.order_id;
            const tone = STATUS_TONE[o.status] || STATUS_TONE.pending;
            const trans = STATUS_NEXT[o.status];
            const phone = o.customer_phone || o.phone || '';
            const masked = phone ? `${phone.slice(0, 4)}…${phone.slice(-3)}` : '';
            const address = o.delivery_address || o.address || '';
            return (
              <article key={id} className="bg-surface border border-surface-border rounded-lg shadow-ambient p-4" data-testid={`mobile-driver-card-${id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-overline text-ink-muted">Order {String(id).slice(0, 8)}</div>
                    <h2 className="font-display text-base text-ink mt-0.5">{o.customer_name || 'Guest'}</h2>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-md border ${tone} font-medium`} data-testid={`mobile-driver-card-${id}-status`}>
                    {o.status || 'pending'}
                  </span>
                </div>

                {address ? (
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-3 flex items-start gap-2 text-sm text-ink hover:text-primary-500"
                    data-testid={`mobile-driver-card-${id}-map`}
                  >
                    <MapPin className="w-4 h-4 text-ink-muted mt-0.5" />
                    <span className="flex-1">{address}</span>
                    <Navigation className="w-3.5 h-3.5 text-ink-muted" />
                  </a>
                ) : null}

                <div className="mt-2 flex items-center gap-4 text-xs text-ink-muted">
                  {phone ? (
                    <a href={`tel:${phone}`} className="flex items-center gap-1 hover:text-primary-500" data-testid={`mobile-driver-card-${id}-phone`}>
                      <Phone className="w-3.5 h-3.5" /> {masked}
                    </a>
                  ) : null}
                  {typeof o.total !== 'undefined' ? (
                    <span className="flex items-center gap-1 font-data tabular-nums">
                      <PoundSterling className="w-3.5 h-3.5" /> {fmtMoney(o.total).slice(1)}
                    </span>
                  ) : null}
                  {o.payment_status ? (
                    <span className={`text-xs ${o.payment_status === 'paid' ? 'text-ok' : 'text-warn'}`}>
                      {o.payment_status}
                    </span>
                  ) : null}
                </div>

                {trans ? (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                      onClick={() => transition(o, trans.next)}
                      disabled={busyId === `${id}:${trans.next}`}
                      className="px-3 py-2 text-sm rounded-md bg-primary-500 text-surface hover:bg-primary-600 disabled:opacity-40 motion-organic"
                      data-testid={`mobile-driver-card-${id}-advance`}
                    >
                      {busyId === `${id}:${trans.next}` ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : <ArrowRight className="w-4 h-4 inline mr-1" />}
                      {trans.label}
                    </button>
                    <button
                      onClick={() => transition(o, 'failed')}
                      disabled={busyId === `${id}:failed`}
                      className="px-3 py-2 text-sm rounded-md border border-danger/40 text-danger hover:bg-danger/5 disabled:opacity-40 motion-organic"
                      data-testid={`mobile-driver-card-${id}-fail`}
                    >
                      <X className="w-4 h-4 inline mr-1" /> Fail
                    </button>
                  </div>
                ) : null}

                {/* POD capture is available the moment a job goes out for delivery — driver
                    can attach proof at handover, not only after manually marking delivered. */}
                {(o.status === 'out_for_delivery' || o.status === 'delivered') ? (
                  <div className="mt-3">
                    {o.status === 'delivered' ? (
                      <div className="flex items-center gap-2 text-xs text-ok mb-2">
                        <Check className="w-4 h-4" /> Delivered.
                      </div>
                    ) : null}
                    {!o.proof_of_delivery ? (
                      <PodCapture orderId={id} headers={headers} onDone={() => reload()} />
                    ) : (
                      <div className="text-xs text-ink-muted flex items-center gap-1">
                        <Check className="w-3.5 h-3.5 text-ok" /> Proof captured · {new Date(o.proof_of_delivery.submitted_at).toLocaleTimeString()}
                      </div>
                    )}
                  </div>
                ) : null}
              </article>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MobileDriverHome;

// ─────────────────────────────────────────────────────────────────────────────
// PodCapture — single-tap proof-of-delivery photo. Uses the device camera via
// `capture="environment"` on the file input. Image is resized + base64-encoded
// client-side, then queued through syncQueue so offline drivers can still
// finish a job and have it sync on reconnect.
// ─────────────────────────────────────────────────────────────────────────────
const _resize = (file, maxDim = 1280, quality = 0.7) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const PodCapture = ({ orderId, headers, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [recipient, setRecipient] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const dataUrl = await _resize(f);
      setPreview(dataUrl);
    } catch {
      setStatus({ kind: 'error', msg: 'Could not read photo' });
    }
  };

  const submit = async () => {
    if (!preview) return;
    setSubmitting(true);
    const base64 = preview.split(',', 2)[1] || preview; // strip "data:image/jpeg;base64,"
    const res = await queueOrSend({
      method: 'POST',
      path: '/api/mobile/delivery/proof-of-delivery',
      headers,
      body: { orderId, photo_base64: base64, recipient_name: recipient || null, notes: notes || null },
    });
    setSubmitting(false);
    if (res.ok) {
      setStatus({ kind: 'ok', msg: 'Proof saved. Job complete.' });
      onDone?.();
    } else if (res.queued) {
      setStatus({ kind: 'queued', msg: 'Offline — proof saved locally, will upload when online' });
      onDone?.();
    } else {
      setStatus({ kind: 'error', msg: res.error || 'Failed to save proof' });
    }
  };

  return (
    <div className="border-t border-surface-border pt-3 mt-2 space-y-2" data-testid={`pod-capture-${orderId}`}>
      {!preview ? (
        <label className="flex items-center gap-2 px-3 py-2.5 bg-primary-50 text-primary-700 rounded-md text-sm font-medium cursor-pointer hover:bg-primary-100 motion-organic" data-testid={`pod-take-photo-${orderId}`}>
          <Camera className="w-4 h-4" />
          Take proof-of-delivery photo
          <input
            type="file" accept="image/*" capture="environment"
            onChange={onFile} className="hidden"
            data-testid={`pod-input-${orderId}`}
          />
        </label>
      ) : (
        <>
          <img src={preview} alt="Proof of delivery preview" className="rounded-md max-h-40 object-cover border border-surface-border" data-testid={`pod-preview-${orderId}`} />
          <input
            type="text" placeholder="Recipient name (optional)"
            value={recipient} onChange={(e) => setRecipient(e.target.value)}
            className="w-full px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm"
            data-testid={`pod-recipient-${orderId}`}
          />
          <input
            type="text" placeholder="Notes (e.g. left at front door)"
            value={notes} onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm"
            data-testid={`pod-notes-${orderId}`}
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => { setPreview(null); setStatus(null); }}
              className="px-3 py-2 text-sm rounded-md border border-surface-border hover:bg-surface-sunken"
              data-testid={`pod-retake-${orderId}`}
            >
              Retake
            </button>
            <button
              onClick={submit} disabled={submitting}
              className="px-3 py-2 text-sm rounded-md bg-primary-500 text-surface hover:bg-primary-600 disabled:opacity-40"
              data-testid={`pod-submit-${orderId}`}
            >
              {submitting ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : <Check className="w-4 h-4 inline mr-1" />}
              Save proof
            </button>
          </div>
        </>
      )}
      {status ? (
        <p
          className={`text-xs ${status.kind === 'ok' ? 'text-ok' : status.kind === 'queued' ? 'text-warn' : 'text-danger'}`}
          data-testid={`pod-status-${orderId}`}
        >
          {status.msg}
        </p>
      ) : null}
    </div>
  );
};
